/**
 * NatalChartDynamic — динамическая SVG-визуализация натальной карты
 *
 * Принимает реальные расчётные данные (planets, houses, aspects)
 * и рисует интерактивное колесо с планетами, домами и аспектами.
 *
 * Особенности:
 * - 12 секторов домов на основе cusps долгот
 * - Планеты на окружности по longitude
 * - Аспекты — цветные линии между планетами
 * - Tooltip при hover с информацией о планете
 * - Вращение колеса колёсиком мыши
 * - Responsive viewBox
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';

// ============================================================
// Типы
// ============================================================

export interface ChartPlanet {
  name: string;
  nameRu: string;
  symbol: string;
  sign: string;
  signRu: string;
  longitude: number;
  degreeInSign: number;
  house: number;
  retrograde: boolean;
}

export interface ChartHouse {
  number: number;
  sign: string;
  signRu: string;
  longitude: number;
  degreeInSign: number;
}

export interface ChartAspect {
  planet1: string;
  planet2: string;
  type: string;
  orb: number;
  applying?: boolean;
}

export interface NatalChartDynamicProps {
  planets: ChartPlanet[];
  houses: ChartHouse[];
  aspects: ChartAspect[];
  ascendantLongitude?: number;
  size?: number;
}

// ============================================================
// Константы
// ============================================================

const ZODIAC_SYMBOLS: Record<string, string> = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋',
  Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏',
  Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
};

const ASPECT_COLORS: Record<string, string> = {
  'Соединение': '#4488FF',
  'conjunction': '#4488FF',
  'Оппозиция': '#FF8844',
  'opposition': '#FF8844',
  'Трин': '#44BB44',
  'trine': '#44BB44',
  'Квадрат': '#FF4444',
  'square': '#FF4444',
  'Секстиль': '#6688FF',
  'sextile': '#6688FF',
  'Квинконс': '#AA44FF',
  'quincunx': '#AA44FF',
  'Полусекстиль': '#88AACC',
  'semi-sextile': '#88AACC',
  'Полуквадрат': '#CC6644',
  'semi-square': '#CC6644',
  'Сесквиквадрат': '#DD7744',
  'sesquiquadrate': '#DD7744',
  'Квинтиль': '#44AACC',
  'quintile': '#44AACC',
  'Биквинтиль': '#44CCAA',
  'bi-quintile': '#44CCAA',
};

/** Радиусы колец (viewBox 0–600) */
const R = {
  outer: 280,
  signInner: 245,
  houseOuter: 245,
  houseInner: 180,
  planet: 155,
  inner: 120,
  center: 30,
};

const CX = 300;
const CY = 300;
const VIEW_SIZE = 600;

// ============================================================
// Утилиты
// ============================================================

function normalizeDeg(deg: number): number {
  deg = deg % 360;
  return deg < 0 ? deg + 360 : deg;
}

/** Конвертация эклиптической долготы в SVG-угол */
function lonToAngle(longitude: number, ascendantLongitude: number): number {
  return normalizeDeg(ascendantLongitude - longitude + 180) * (Math.PI / 180);
}

/** Точка на окружности */
function pointOnCircle(
  longitude: number,
  radius: number,
  ascendantLongitude: number,
): { x: number; y: number } {
  const angle = lonToAngle(longitude, ascendantLongitude);
  return {
    x: CX + radius * Math.cos(angle),
    y: CY - radius * Math.sin(angle),
  };
}

// ============================================================
// Компонент
// ============================================================

const NatalChartDynamic: React.FC<NatalChartDynamicProps> = ({
  planets,
  houses,
  aspects,
  ascendantLongitude = 0,
  size = 500,
}) => {
  const [hoveredPlanet, setHoveredPlanet] = useState<string | null>(null);
  const [selectedPlanet, setSelectedPlanet] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  const svgRef = useRef<SVGSVGElement>(null);

  const activePlanet = hoveredPlanet || selectedPlanet;
  const activePlanetData = planets.find((p) => p.name === activePlanet);

  // Подсвеченные аспекты для выбранной планеты
  const highlightedAspectKeys = useMemo(() => {
    if (!selectedPlanet) return new Set<string>();
    return new Set(
      aspects
        .filter((a) => a.planet1 === selectedPlanet || a.planet2 === selectedPlanet)
        .map((a) => `${a.planet1}-${a.planet2}`),
    );
  }, [selectedPlanet, aspects]);

  // Позиции планет с предотвращением наложений
  const planetPositions = useMemo(() => {
    const sorted = [...planets].sort((a, b) => a.longitude - b.longitude);
    const positions = new Map<string, { x: number; y: number; angle: number }>();
    const minAngleDeg = 8;
    const minAngleRad = minAngleDeg * (Math.PI / 180);

    for (const planet of sorted) {
      let adjustedLon = planet.longitude;

      if (positions.size > 0) {
        const lastPlaced = sorted.slice(0, sorted.indexOf(planet)).filter((p) => positions.has(p.name));
        if (lastPlaced.length > 0) {
          const prev = lastPlaced[lastPlaced.length - 1];
          const prevPos = positions.get(prev.name);
          if (prevPos) {
            let currentAngle = lonToAngle(adjustedLon, ascendantLongitude);
            let diff = prevPos.angle - currentAngle;
            if (diff < 0) diff += 2 * Math.PI;
            if (diff < minAngleRad) {
              const newAngle = prevPos.angle - minAngleRad;
              adjustedLon = normalizeDeg(ascendantLongitude + 180 - (newAngle * 180) / Math.PI);
            }
          }
        }
      }

      const pos = pointOnCircle(adjustedLon, R.planet, ascendantLongitude);
      positions.set(planet.name, {
        x: pos.x,
        y: pos.y,
        angle: lonToAngle(adjustedLon, ascendantLongitude),
      });
    }

    return positions;
  }, [planets, ascendantLongitude]);

  // Обработка колёсика мыши для вращения
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      setRotation((prev) => normalizeDeg(prev + e.deltaY * 0.1));
    },
    [],
  );

  // Двойной клик — сброс вращения
  const handleDoubleClick = useCallback(() => {
    setRotation(0);
  }, []);

  // ============================================================
  // Render
  // ============================================================

  return (
    <div
      style={{
        position: 'relative',
        width: size,
        maxWidth: '100%',
        margin: '0 auto',
      }}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`}
        width={size}
        height={size}
        style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }}
        onWheel={handleWheel}
        onDoubleClick={handleDoubleClick}
        role="img"
        aria-label="Натальная карта"
      >
        {/* Градиенты и фильтры */}
        <defs>
          <radialGradient id="natalBgGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1a1a2e" />
            <stop offset="100%" stopColor="#0f0f1a" />
          </radialGradient>
          <radialGradient id="natalCenterGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#d4af37" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#d4af37" stopOpacity="0" />
          </radialGradient>
          <filter id="natalGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="natalSoftGlow">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Фон */}
        <circle cx={CX} cy={CY} r={R.outer + 15} fill="url(#natalBgGradient)" />

        {/* Вращаемая группа */}
        <g transform={`rotate(${rotation} ${CX} ${CY})`}>
          {/* === КОЛЬЦО ЗНАКОВ === */}
          <circle cx={CX} cy={CY} r={R.outer} fill="none" stroke="#d4af37" strokeWidth="2" opacity="0.6" />
          <circle cx={CX} cy={CY} r={R.signInner} fill="none" stroke="#d4af37" strokeWidth="1.5" opacity="0.4" />

          {/* Символы знаков */}
          {Object.entries(ZODIAC_SYMBOLS).map(([signEn, symbol]) => {
            const signIndex = Object.keys(ZODIAC_SYMBOLS).indexOf(signEn);
            const midLon = signIndex * 30 + 15;
            const pos = pointOnCircle(midLon, (R.outer + R.signInner) / 2, ascendantLongitude);
            return (
              <text
                key={`sign-${signEn}`}
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#d4af37"
                fontSize="22"
                opacity="0.85"
                style={{ fontFamily: 'serif' }}
              >
                {symbol}
              </text>
            );
          })}

          {/* Разделители знаков */}
          {Array.from({ length: 12 }).map((_, i) => {
            const lon = i * 30;
            const inner = pointOnCircle(lon, R.signInner, ascendantLongitude);
            const outer = pointOnCircle(lon, R.outer, ascendantLongitude);
            return (
              <line
                key={`sign-line-${i}`}
                x1={inner.x}
                y1={inner.y}
                x2={outer.x}
                y2={outer.y}
                stroke="#d4af37"
                strokeWidth="1"
                opacity="0.5"
              />
            );
          })}

          {/* === КОЛЬЦО ДОМОВ === */}
          <circle cx={CX} cy={CY} r={R.houseOuter} fill="none" stroke="#d4af37" strokeWidth="1.5" opacity="0.3" />
          <circle cx={CX} cy={CY} r={R.houseInner} fill="none" stroke="#d4af37" strokeWidth="1.5" opacity="0.4" />

          {/* Линии домов */}
          {houses.map((house) => {
            const inner = pointOnCircle(house.longitude, R.houseInner, ascendantLongitude);
            const outer = pointOnCircle(house.longitude, R.houseOuter, ascendantLongitude);
            const isAngular = [1, 4, 7, 10].includes(house.number);
            return (
              <line
                key={`house-${house.number}`}
                x1={inner.x}
                y1={inner.y}
                x2={outer.x}
                y2={outer.y}
                stroke={isAngular ? '#d4af37' : '#8888aa'}
                strokeWidth={isAngular ? 2 : 1}
                opacity={isAngular ? 0.8 : 0.4}
              />
            );
          })}

          {/* Номера домов */}
          {houses.map((house) => {
            const nextIdx = house.number % houses.length;
            const nextHouse = houses[nextIdx];
            let midLon = (house.longitude + nextHouse.longitude) / 2;
            if (Math.abs(house.longitude - nextHouse.longitude) > 180) {
              midLon = normalizeDeg((house.longitude + nextHouse.longitude + 360) / 2);
            }
            const pos = pointOnCircle(midLon, (R.houseOuter + R.houseInner) / 2, ascendantLongitude);
            return (
              <text
                key={`house-num-${house.number}`}
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#8888aa"
                fontSize="11"
                opacity="0.6"
              >
                {house.number}
              </text>
            );
          })}

          {/* Внутреннее кольцо */}
          <circle cx={CX} cy={CY} r={R.inner} fill="none" stroke="#d4af37" strokeWidth="1" opacity="0.2" />

          {/* === АСПЕКТЫ === */}
          {aspects.map((aspect, idx) => {
            const pos1 = planetPositions.get(aspect.planet1);
            const pos2 = planetPositions.get(aspect.planet2);
            if (!pos1 || !pos2) return null;

            const color = ASPECT_COLORS[aspect.type] || '#666666';
            const key = `${aspect.planet1}-${aspect.planet2}`;
            const isHighlighted = highlightedAspectKeys.has(key);
            const opacity = activePlanet
              ? (isHighlighted ? 0.8 : 0.08)
              : 0.35;

            const isHard = ['Квадрат', 'square', 'Оппозиция', 'opposition'].includes(aspect.type);

            return (
              <line
                key={`aspect-${idx}`}
                x1={pos1.x}
                y1={pos1.y}
                x2={pos2.x}
                y2={pos2.y}
                stroke={color}
                strokeWidth={isHighlighted ? 2.5 : 1}
                opacity={opacity}
                strokeDasharray={isHard ? '5,3' : undefined}
              />
            );
          })}

          {/* === ПЛАНЕТЫ === */}
          {planets.map((planet) => {
            const pos = planetPositions.get(planet.name);
            if (!pos) return null;

            const isActive = activePlanet === planet.name;

            return (
              <g
                key={`planet-${planet.name}`}
                onMouseEnter={() => setHoveredPlanet(planet.name)}
                onMouseLeave={() => setHoveredPlanet(null)}
                onClick={() =>
                  setSelectedPlanet((prev) => (prev === planet.name ? null : planet.name))
                }
                style={{ cursor: 'pointer' }}
                role="button"
                aria-label={`${planet.nameRu} в ${planet.signRu}, ${planet.house} дом`}
              >
                {/* Glow при активности */}
                {isActive && (
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r="18"
                    fill="#d4af37"
                    opacity="0.15"
                    filter="url(#natalGlow)"
                  />
                )}

                {/* Символ планеты */}
                <text
                  x={pos.x}
                  y={pos.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={isActive ? '#FFD700' : '#d4af37'}
                  fontSize={isActive ? '22' : '17'}
                  fontWeight={isActive ? 'bold' : 'normal'}
                  opacity={isActive ? 1 : 0.85}
                  filter={isActive ? 'url(#natalSoftGlow)' : undefined}
                  style={{
                    fontFamily: 'serif',
                    transition: 'all 0.2s ease',
                    userSelect: 'none',
                  }}
                >
                  {planet.symbol}
                </text>

                {/* Ретроградный маркер */}
                {planet.retrograde && (
                  <text
                    x={pos.x + 12}
                    y={pos.y - 10}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="#FF6666"
                    fontSize="8"
                    fontWeight="bold"
                    opacity="0.9"
                  >
                    R
                  </text>
                )}
              </g>
            );
          })}

          {/* === УГЛОВЫЕ ТОЧКИ (ASC, MC, DSC, IC) === */}
          {[
            { label: 'ASC', longitude: ascendantLongitude },
            { label: 'MC', longitude: normalizeDeg(ascendantLongitude + 90) },
            { label: 'DSC', longitude: normalizeDeg(ascendantLongitude + 180) },
            { label: 'IC', longitude: normalizeDeg(ascendantLongitude + 270) },
          ].map((point) => {
            const pos = pointOnCircle(point.longitude, R.outer + 14, ascendantLongitude);
            return (
              <text
                key={point.label}
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#FFD700"
                fontSize="10"
                fontWeight="bold"
                opacity="0.7"
              >
                {point.label}
              </text>
            );
          })}
        </g>

        {/* Центр (не вращается) */}
        <circle cx={CX} cy={CY} r={R.center} fill="url(#natalCenterGlow)" />
        <circle cx={CX} cy={CY} r="4" fill="#d4af37" opacity="0.8" />

        {/* Индикатор вращения */}
        {Math.abs(rotation) > 0.5 && (
          <text
            x={CX}
            y={CY + 20}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#d4af37"
            fontSize="10"
            opacity="0.5"
          >
            {rotation.toFixed(1)}°
          </text>
        )}
      </svg>

      {/* Tooltip */}
      {activePlanetData && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(10, 8, 20, 0.95)',
            border: '1px solid rgba(212, 175, 55, 0.5)',
            borderRadius: 10,
            padding: '10px 18px',
            color: '#e0e0e0',
            fontSize: 13,
            textAlign: 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
            zIndex: 10,
          }}
        >
          <span style={{ fontSize: 18, marginRight: 6 }}>{activePlanetData.symbol}</span>
          <strong style={{ color: '#FFD700' }}>{activePlanetData.nameRu}</strong>
          <span style={{ color: '#888', margin: '0 6px' }}>·</span>
          <span>{activePlanetData.signRu}</span>
          <span style={{ color: '#888', margin: '0 4px' }}>
            {activePlanetData.degreeInSign.toFixed(1)}°
          </span>
          <span style={{ color: '#888', margin: '0 6px' }}>·</span>
          <span>{activePlanetData.house} дом</span>
          {activePlanetData.retrograde && (
            <>
              <span style={{ color: '#888', margin: '0 6px' }}>·</span>
              <span style={{ color: '#FF6666', fontWeight: 600 }}>ретроградная</span>
            </>
          )}
        </div>
      )}

      {/* Подсказка по управлению */}
      <div
        style={{
          textAlign: 'center',
          marginTop: 8,
          color: '#888',
          fontSize: 11,
          fontFamily: 'var(--font-sans, sans-serif)',
        }}
      >
        Колёсико — вращение · Двойной клик — сброс · Клик на планету — подсветить аспекты
      </div>
    </div>
  );
};

export default NatalChartDynamic;
